import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';
import { Trophy, Calendar, ArrowLeft } from 'lucide-react';

const Winners = () => {
  const [allWinners, setAllWinners] = useState([]);
  const [currentWinner, setCurrentWinner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWinners = async () => {
      try {
        // Fetch all winners from the API (archive page needs the full list, not just the last 4)
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/winners?limit=10000`);
        if (res.ok) {
          const data = await res.json();
          setCurrentWinner(data.current_winner);
          // Combine current and past for archive display
          const all = [];
          if (data.current_winner) {
            all.push({ ...data.current_winner, is_current: true });
          }
          if (data.past_winners) {
            all.push(...data.past_winners.map(w => ({ ...w, is_current: false })));
          }
          setAllWinners(all);
        }
      } catch (error) {
        console.error('Error fetching winners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWinners();
  }, []);

  // Helper function to get week date range
  const getWeekDateRange = (dateStr) => {
    const date = dateStr ? new Date(dateStr) : new Date();
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const month = weekStart.toLocaleString('default', { month: 'long' });
    return `Week of ${month} ${weekStart.getDate()}-${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
  };

  // Group all winners by week (show all, with or without photos)
  const groupedWinners = allWinners.reduce((groups, winner) => {
    const key = getWeekDateRange(winner.win_date || winner.created_at);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(winner);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-4xl font-black">Loading...</div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pt-32"
      style={{
        backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
      }}
    >
      <SEO 
        title="Weekly Drawing Winners"
        description="See all our weekly drawing winners at 1st Ave ICP! Enter for your chance to win free ice cream and exclusive offers."
        keywords="weekly drawing winners, ice cream contest, Mineral Wells TX giveaway"
        canonical="/winners"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#E91E8C] to-[#9B4DCA] pt-16 pb-24 px-6">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="flex justify-center mb-6">
            <Trophy className="w-16 h-16" />
          </div>
          <h1 
            className="text-4xl md:text-6xl font-bold mb-4"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            Our Winners!
          </h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto">
            Congratulations to all our weekly drawing winners! Enter for your chance to be featured here.
          </p>
        </div>
        
        {/* Wavy Bottom Edge */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-16" viewBox="0 0 1440 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path 
              fill="#ffffff" 
              d="M0,32 C360,64 720,0 1080,32 C1260,48 1380,48 1440,32 L1440,64 L0,64 Z"
            />
          </svg>
        </div>
      </section>

      {/* Current Winner Spotlight - Show with photo or placeholder */}
      {currentWinner && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-block bg-[#E91E8C] text-white px-4 py-1 rounded-full text-sm font-bold">
                🎉 THIS WEEK&apos;S WINNER
              </span>
            </div>
            
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 p-8 md:p-12 rounded-3xl border-4 border-[#E91E8C] shadow-2xl text-center">
              {currentWinner.image_url ? (
                <div className="w-32 h-32 md:w-40 md:h-40 mx-auto rounded-full overflow-hidden border-4 border-[#E91E8C] mb-6 shadow-lg">
                  <OptimizedImage 
                    src={currentWinner.image_url} 
                    alt={currentWinner.name}
                    className="w-full h-full object-cover"
                    width={160}
                    height={160}
                  />
                </div>
              ) : (
                <div className="w-32 h-32 md:w-40 md:h-40 mx-auto rounded-full bg-gradient-to-br from-pink-200 to-purple-200 border-4 border-[#E91E8C] mb-6 flex items-center justify-center">
                  <div className="text-center">
                    <span className="text-4xl">📸</span>
                    <p className="text-sm text-gray-500 font-medium mt-1">Photo Coming Soon</p>
                  </div>
                </div>
              )}
              <h2 
                className="text-3xl md:text-4xl font-black text-[#E91E8C] mb-2"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                {currentWinner.name}
              </h2>
              {currentWinner.prize && (
                <p className="text-xl text-gray-600">Won: {currentWinner.prize}</p>
              )}
              <p className="text-sm text-gray-400 mt-4 flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                {getWeekDateRange(currentWinner.win_date || currentWinner.created_at)}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Winners Archive */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-12"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            Winners Archive
          </h2>
          
          {allWinners.length === 0 ? (
            <div className="text-center py-16">
              <Trophy className="w-20 h-20 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">No winners yet!</p>
              <p className="text-gray-400 mt-2">Be the first - enter our weekly drawing on the homepage!</p>
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 mt-6 bg-[#E91E8C] text-white px-6 py-3 rounded-full font-bold hover:bg-[#C4197A] transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Enter the Drawing
              </Link>
            </div>
          ) : (
            <div className="space-y-12">
              {Object.entries(groupedWinners).map(([weekDate, winners]) => (
                <div key={weekDate}>
                  <h3 className="text-xl font-bold text-gray-700 mb-6 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#E91E8C]" />
                    {weekDate}
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {winners.map((winner, idx) => (
                      <div 
                        key={winner.id || idx}
                        className={`bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all ${
                          winner.is_current ? 'ring-4 ring-[#E91E8C] ring-opacity-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-full overflow-hidden bg-pink-100 flex-shrink-0 flex items-center justify-center">
                            {winner.image_url ? (
                              <img src={winner.image_url} alt={winner.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="text-center">
                                <span className="text-xl">📸</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-black text-lg">{winner.name}</h4>
                              {winner.is_current && (
                                <span className="bg-[#E91E8C] text-white text-xs px-2 py-0.5 rounded-full">
                                  Current
                                </span>
                              )}
                            </div>
                            {winner.prize && (
                              <p className="text-gray-600 text-sm">🎁 {winner.prize}</p>
                            )}
                            {!winner.image_url && (
                              <p className="text-xs text-gray-400 italic">Photo coming soon</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#E91E8C] to-[#9B4DCA]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            Want to Be Our Next Winner?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Enter our weekly drawing for a chance to win free ice cream and exclusive offers!
          </p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-white text-[#E91E8C] px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            Enter the Drawing
          </Link>
        </div>
      </section>

      {/* Bottom Padding */}
      <div className="h-16 bg-white"></div>
    </div>
  );
};

export default Winners;
